import * as React from 'react'
import * as Yup from 'yup'
import {
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  CardMedia,
} from '@mui/material'
import PageTitle from '../../components/title/PageTitle'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useState, useEffect } from 'react'
import AxiosClient from '../../utils/axios'
import { useNavigate, useParams } from 'react-router-dom'
import LoadingBar from '../../components/loading/LoadingBar'
import ErrorMessage from '../../utils/errorMessage'
import HttpErrorNotification from '../../components/notifications/HttpErrorNotification'
import Breadcrumb from '../../components/breadcrumb/Breadcrumb'

export default function EditProduct() {
  const navigate = useNavigate()
  const params = useParams()
  const [sendRequest, setSendRequest] = useState(false)
  const [categories, setCategories] = useState([])
  const [alert, setAlert] = useState({ state: false, message: '' })
  const [category, setCategory] = useState('')
  const [oldPicture, setOldPicture] = useState('')

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Mahsulot nomini yozing'),
    description: Yup.string(),
    price: Yup.number()
      .required('Narxni yozing')
      .min(0, "Narx manfiy qiymat bo'lmasligi kerak")
      .integer("Narx butun qiymat bo'lishi kerak")
      .default(() => 0),
    picture: Yup.string(),
    minimumOrder: Yup.number()
      .required('Minium buyurtma sonini kiriting')
      .min(1, "Kamida 1 bo'lish kerak")
      .max(20, "Maximum 20 bo'lish kerak")
      .integer('Butun qiymat kiriting'),
    maximumOrder: Yup.number()
      .required('Maximum buyurtma sonini kiriting')
      .min(1, "Kamida 1 bo'lish kerak")
      .max(20, "Maximum 20 bo'lish kerak")
      .integer('Butun qiymat kiriting'),
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async (body) => {
    try {
      await AxiosClient.put(`/product/${params.productId}`, {
        ...body,
        category,
      })
      navigate('/product')
    } catch (error) {
      const message = ErrorMessage(error)
      setAlert({
        state: true,
        message,
      })
      setSendRequest(false)
    }
  }

  const fetch = async () => {
    const product = await AxiosClient.get(`/product/${params.productId}`)
    const categories = await AxiosClient.get('/category')
    return { product: product.data, categories: categories.data }
  }

  useEffect(() => {
    setSendRequest(true)
    fetch()
      .then(
        (data) => {
          setCategories(data.categories)
          setCategory(data.product.category._id)
          setValue('name', data.product.name)
          setValue('description', data.product.description)
          setValue('price', data.product.price)
          setValue('picture', data.product.picture)
          setOldPicture(data.product.picture)
          setValue('minimumOrder', data.product.min)
          setValue('maximumOrder', data.product.max)
        },
        (error) => {
          const message = ErrorMessage(error)
          setAlert({
            state: true,
            message,
          })
        },
      )
      .then(() => setSendRequest(false))
  }, [])

  const handleCategoryChange = (event) => {
    setCategory(event.target.value)
  }

  return (
    <Paper sx={{ p: 1 }}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <PageTitle title="Mahsulotni o'zgartirish" />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id="name"
            name="name"
            label="Mahsulot nomi"
            {...register('name')}
            error={errors.name ? true : false}
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="inherit" color="textSecondary">
            {errors.name?.message}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            id="description"
            name="description"
            label="Izoh"
            {...register('description')}
            error={errors.description ? true : false}
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="inherit" color="textSecondary">
            {errors.description?.message}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            id="price"
            name="price"
            label="Narx"
            {...register('price')}
            error={errors.price ? true : false}
            type="number"
            defaultValue="0"
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="inherit" color="textSecondary">
            {errors.price?.message}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            id="picture"
            name="picture"
            label="Rasm"
            {...register('picture')}
            error={errors.picture ? true : false}
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="inherit" color="textSecondary">
            {errors.picture?.message}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <CardMedia component="img" image={oldPicture} alt="mahsulot rasmi" />
        </Grid>
        <Grid item xs={12}>
          <InputLabel id="category-label">Kategoriya</InputLabel>
          <Select
            labelId="category-label"
            id="category"
            value={category}
            label="Kategoriya"
            onChange={handleCategoryChange}
          >
            {categories.map((category) => {
              return (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              )
            })}
          </Select>
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            id="minimumOrder"
            name="minimumOrder"
            label="Minium buyurtma qilish soni"
            {...register('minimumOrder')}
            error={errors.minimumOrder ? true : false}
            type="number"
            defaultValue="1"
            margin="dense"
            sx={{ width: '30ch' }}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="inherit" color="textSecondary">
            {errors.minimumOrder?.message}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            id="maximumOrder"
            name="maximumOrder"
            label="Maximum buyurtma qilish soni"
            {...register('maximumOrder')}
            error={errors.maximumOrder ? true : false}
            type="number"
            defaultValue="1"
            margin="dense"
            sx={{ width: '30ch' }}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="inherit" color="textSecondary">
            {errors.maximumOrder?.message}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmit)}
          >
            Saqlash
          </Button>
        </Grid>
        <Grid item xs={12}>
          {alert.state ? (
            <HttpErrorNotification message={alert.message} />
          ) : (
            <></>
          )}
        </Grid>

        {sendRequest ? <LoadingBar /> : <></>}
        <Breadcrumb />
      </Grid>
    </Paper>
  )
}
